import { useNavigate } from "react-router-dom";

import { Models, OrderDeliveryStatus, OrderPickupStatus } from "api/types";
import { getUpcomingOrders } from "utils/retailProducts.helpers";
import {
  getDeliveryProgress,
  getPickupProgress,
} from "utils/retailProducts.helpers";

import {
  RetailProductDeliverySection,
  RetailProductPickupSection,
} from "../index";

export interface RetailProductUpcomingOrdersSectionProps {
  productsOrders: Models<"DisplayRetailProductOrderFromPerson">[];
  platformLocations: Models<"PlatformLocationModel1">[];
}

export const RetailProductUpcomingOrdersSection = ({
  productsOrders,
  platformLocations,
}: RetailProductUpcomingOrdersSectionProps) => {
  const navigate = useNavigate();
  const upcomingOrders = getUpcomingOrders(productsOrders);

  if (upcomingOrders.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-1 gap-4">
        {upcomingOrders.map((order) => {
          const locationName =
            platformLocations.find(
              (location) => location.id === order.locationId
            )?.name || "Store";

          const totalItems =
            order.lineItemsForPickup.length + order.lineItemsForShipping.length;

          // For mixed orders, show all pickup items; for pickup-only orders, show only if there are items waiting to be redeemed
          const pickupItems =
            totalItems > 1
              ? order.lineItemsForPickup
              : order.lineItemsForPickup.filter(
                  (item) => item.status !== OrderPickupStatus.PickedUp
                );

          const deliveryItems = order.lineItemsForShipping.filter(
            (item) => item.status === OrderDeliveryStatus.Pending
          );

          const pickupProgress = getPickupProgress(pickupItems);
          const deliveryProgress = getDeliveryProgress(deliveryItems);

          return (
            <div
              key={order.orderID}
              className="cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition-all duration-200 hover:shadow-md active:scale-[0.98]"
              onClick={() =>
                navigate(
                  `/wallet/products/order/${order.orderID}?type=upcoming`
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  navigate(
                    `/wallet/products/order/${order.orderID}?type=upcoming`
                  );
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="p-4">
                <div className="space-y-4">
                  {pickupItems.length > 0 && (
                    <RetailProductPickupSection
                      context="wallet"
                      pickupItems={pickupItems}
                      pickupProgress={pickupProgress}
                      locationName={locationName}
                      orderType="upcoming"
                      orderId={order.orderID}
                    />
                  )}
                  {deliveryItems.length > 0 && (
                    <>
                      {pickupItems.length > 0 && deliveryItems.length > 0 && (
                        <div className="-mx-4 border-t border-gray-300" />
                      )}
                      <RetailProductDeliverySection
                        context="wallet"
                        deliveryItems={deliveryItems}
                        deliveryProgress={deliveryProgress}
                        orderType="upcoming"
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="w-full bg-brand-color/10 py-2 text-center text-body3 text-brand-color">
                View Details
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
